import { CommonModule } from '@angular/common';
import { Component, effect, input, model, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

export interface SelectionItemWithId {
  id: string;
  text: string;
}

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
})
export class MultiSelectComponent<TItem extends SelectionItemWithId> {
  public index = input.required<number>();
  public items = input.required<TItem[]>();
  public title = input.required<string>();
  public loading = input<boolean>(false);
  public selection = model<TItem[]>([]);

  private index$: Observable<number>;

  protected show = signal(true);

  constructor() {
    this.index$ = toObservable(this.index);

    effect(() => {
      const items = this.items();
      const selected = this.selection();
      if (selected.length === 0) {
        return;
      }

      const itemById = new Map(items.map((item) => [item.id, item]));
      const normalizedSelection = selected
        .map((selectedItem) => itemById.get(selectedItem.id))
        .filter((item): item is TItem => item !== undefined);

      if (normalizedSelection.length !== selected.length) {
        this.selection.set(normalizedSelection);
        return;
      }

      const referencesMatch = normalizedSelection.every(
        (item, index) => item === selected[index],
      );
      if (!referencesMatch) {
        this.selection.set(normalizedSelection);
      }
    });
  }

  protected isSelected(item: TItem): boolean {
    return this.selection().some((selectedItem) => selectedItem.id === item.id);
  }

  onSelectionChange(item: TItem) {
    const current = this.selection();
    if (this.isSelected(item)) {
      this.selection.set(
        current.filter((selectedItem) => selectedItem.id !== item.id),
      );
    } else {
      this.selection.set([...current, item]);
    }
  }

  onSelectAll() {
    this.selection.set([...this.items()]);
  }

  onDeselectAll() {
    this.selection.set([]);
  }
}
